import { isEmpty } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useRewardTierApplied } from '@fhs/loyalty/src/hooks/use-reward-tier-applied';
import { useCartLimit } from '@fhs-legacy/frontend/src/hooks/use-cart-limit';
import { useLoyaltyIncentivesAvailability } from '@fhs-legacy/frontend/src/pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import { REGIONS } from '@fhs-legacy/frontend/src/state/intl/types';
import { IncentiveEvaluationErrorCodes } from '@fhs-legacy/frontend/src/state/loyalty/hooks/types';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { region } from '@fhs-legacy/frontend/src/utils/environment';

/**
 * Determines if the cart continue button should be disabled when there are no cart entries, reward or offers have errors, no discount is applied,
 * the cart total is negative, above max, below reward minimum subtotal, or below minimum (for delivery). The button is disabled when cart total is undefined or null to prevent pricing and
 * loading errors encountered in the past
 */
export function useContinueButtonStates() {
  const { cartEntries, shouldSkipPriceOrder } = useCartContext();
  const { selectedDonationId, calculatedCartTotal, reorder } = useOrderContext();
  const isReorderLoading = reorder?.loadingEnrichCartEntries || false;

  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Set a small timeout to allow cart page to fully render before allowing continue enabled
    timeoutRef.current = setTimeout(() => {
      setIsInitialLoadComplete(true);
    }, 500);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const { rewardTierApplied } = useRewardTierApplied();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const appliedRewardsFeedbackMap = useAppSelector(
    selectors.loyalty.selectAppliedRewardsFeedbackMap
  );

  const selectedOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);
  const isDiscountNotApplied = useAppSelector(selectors.loyalty.selectIsDiscountNotApplied);
  const filteredOfferErrorList = useAppSelector(state =>
    selectors.loyalty.selectIncentiveErrorsFilteredByCode(
      state,
      IncentiveEvaluationErrorCodes.INVALID_PAYMENT_METHOD
    )
  );

  // INVALID INCENTIVES
  // rewards
  const hasRewardErrors =
  !isEmpty(appliedRewardsFeedbackMap) &&
  appliedOffers.some(offer => offer.id === rewardTierApplied?.id);

  //offers
  const { checkLoyaltyOfferAvailability } = useLoyaltyIncentivesAvailability();

  const availableOffer = selectedOffer ? checkLoyaltyOfferAvailability(selectedOffer) : false;
  const ruleErrors = offersFeedbackMap[selectedOffer?.loyaltyEngineId || ''];
  const hasOfferRuleErrors = Boolean(ruleErrors?.length);
  const hasOfferErrors = hasOfferRuleErrors || (selectedOffer && !availableOffer);

  const invalidIncentives = !!filteredOfferErrorList?.length || hasOfferErrors || hasRewardErrors;

  const hasAppliedRewards = useMemo(() => {
    if (!rewardTierApplied) return false;
    return appliedOffers.some(offer => offer.id === rewardTierApplied.id);
  }, [appliedOffers, rewardTierApplied]);

  const effectiveRegion = region();
  const isCanadaStore = effectiveRegion === REGIONS.CA;

  const donationAmount = useMemo(() => {
    return cartEntries.find(entry => entry.isDonation)?.price ?? 0;
  }, [cartEntries]);

  // CART SUBTOTAL NEGATIVE
  const isCartTotalNegative = calculatedCartTotal < 0;

  // OVER CART MAX LIMIT
  const { isOverCartLimit } = useCartLimit(); // TODO: rewrite this one

  // CART SUBTOTAL UNDEFINED OR NULL
  const isCartSubTotalUndefined = calculatedCartTotal === undefined || calculatedCartTotal === null;
  const isBelowRewardMinimumSubtotal =
    !isCanadaStore &&
    hasAppliedRewards &&
    !isCartSubTotalUndefined &&
    calculatedCartTotal - donationAmount < 1;

  const isDisabledForBadState = useMemo(() => {
    return (
      !isInitialLoadComplete ||
      invalidIncentives ||
      isCartTotalNegative ||
      isDiscountNotApplied ||
      !cartEntries.length ||
      isOverCartLimit ||
      isCartSubTotalUndefined ||
      isBelowRewardMinimumSubtotal ||
      isReorderLoading ||
      shouldSkipPriceOrder
    );
  }, [
    isInitialLoadComplete,
    invalidIncentives,
    isCartTotalNegative,
    isDiscountNotApplied,
    cartEntries.length,
    isOverCartLimit,
    isCartSubTotalUndefined,
    isBelowRewardMinimumSubtotal,
    isReorderLoading,
    shouldSkipPriceOrder,
  ]);

  const isLoading = useMemo(() => {
    return !isInitialLoadComplete || isReorderLoading;
  }, [isInitialLoadComplete, isReorderLoading]);

  return {
    isDisabledForBadState,
    isLoading,
    shouldContinue: !!selectedDonationId,
  };
}
